import { Injectable } from '@angular/core';
import * as RecordRTC from "recordrtc";
import { Observable, Subject } from "rxjs";
import * as moment from 'moment'
// @Injectable({
//   providedIn: 'root'
// })

interface RecordedAudioOutput {
  blob: Blob;
  title: string;
}

@Injectable()
export class AudioRecorderService {
  readonly barCount = 11;
  private stream;
  private recorder;
  private interval;
  private startTime;
  private audioContext: AudioContext;
  private analyser: AnalyserNode;
  private analyserSource: MediaStreamAudioSourceNode;
  private analyserFrame: number;
  private _recorded = new Subject<any>();
  private _recordingTime = new Subject<any>();
  private _recordingFailed = new Subject<any>();
  private _levels = new Subject<number[]>();
  constructor() { }
  getRecordedBlob(): Observable<RecordedAudioOutput> {
    return this._recorded.asObservable();
  }

  getRecordedTime(): Observable<string> {
    return this._recordingTime.asObservable();
  }

  recordingFailed(): Observable<string> {
    return this._recordingFailed.asObservable();
  }

  getLevels(): Observable<number[]> {
    return this._levels.asObservable();
  }

  startRecording() {
    if (this.recorder) {
      // It means recording is already started or it is already recording something
      return;
    }

    this._recordingTime.next("00:00");
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(s => {
        this.stream = s;
        this.record();
      })
      .catch(error => {
        this._recordingFailed.next(true);
      });
  }

  abortRecording() {
    this.stopMedia();
  }

  private record() {
    this.recorder = new RecordRTC.StereoAudioRecorder(this.stream, {
      type: "audio",
      mimeType: "audio/webm"
    });

    this.recorder.record();
    this.startAnalyser();
    this.startTime = moment();
    this.interval = setInterval(() => {
      const currentTime = moment();
      const diffTime = moment.duration(currentTime.diff(this.startTime));
      const time =
        this.toString(diffTime.minutes()) +
        ":" +
        this.toString(diffTime.seconds());
      this._recordingTime.next(time);
    }, 1000);
  }

  private startAnalyser() {
    this.stopAnalyser();
    if (!this.stream) {
      return;
    }
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) {
      return;
    }
    this.audioContext = new AudioCtx();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.55;
    this.analyserSource = this.audioContext.createMediaStreamSource(this.stream);
    this.analyserSource.connect(this.analyser);
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    this.tickAnalyser();
  }

  private tickAnalyser = () => {
    if (!this.analyser) {
      return;
    }
    const freq = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(freq);
    const time = new Uint8Array(this.analyser.fftSize);
    this.analyser.getByteTimeDomainData(time);

    let rms = 0;
    for (let i = 0; i < time.length; i++) {
      const v = (time[i] - 128) / 128;
      rms += v * v;
    }
    rms = Math.sqrt(rms / time.length);
    const volume = rms < 0.04 ? 0 : Math.min(1, (rms - 0.04) / 0.28);

    const startBin = 2;
    const endBin = Math.min(freq.length, 48);
    const usable = endBin - startBin;
    const bars = this.barCount;
    const bucket = Math.max(1, Math.floor(usable / bars));
    const levels: number[] = [];
    for (let i = 0; i < bars; i++) {
      let sum = 0;
      const offset = startBin + i * bucket;
      for (let j = 0; j < bucket; j++) {
        sum += freq[offset + j] || 0;
      }
      const band = sum / bucket / 255;
      const gated = volume === 0 ? 0 : Math.min(1, band * 0.55 + volume * 0.7);
      levels.push(gated);
    }
    this._levels.next(levels);
    this.analyserFrame = requestAnimationFrame(this.tickAnalyser);
  };

  private stopAnalyser() {
    if (this.analyserFrame) {
      cancelAnimationFrame(this.analyserFrame);
      this.analyserFrame = null;
    }
    if (this.analyserSource) {
      try { this.analyserSource.disconnect(); } catch (e) {}
      this.analyserSource = null;
    }
    this.analyser = null;
    if (this.audioContext) {
      const ctx = this.audioContext;
      this.audioContext = null;
      ctx.close().catch(() => {});
    }
    this._levels.next(new Array(this.barCount).fill(0));
  }

  private toString(value) {
    let val = value;
    if (!value) val = "00";
    if (value < 10) val = "0" + value;
    return val;
  }

  stopRecording() {
    if (this.recorder) {
      this.recorder.stop(
        blob => {
          if (this.startTime) {
            const mp3Name = encodeURIComponent(
              "audio_" + new Date().getTime() + ".mp3"
            );
            this.stopMedia();
            this._recorded.next({ blob: blob, title: mp3Name });
          }
        },
        () => {
          this.stopMedia();
          this._recordingFailed.next(true);
        }
      );
    }
  }

  private stopMedia() {
    this.stopAnalyser();
    if (this.recorder) {
      this.recorder = null;
      clearInterval(this.interval);
      this.startTime = null;
      if (this.stream) {
        this.stream.getAudioTracks().forEach(track => track.stop());
        this.stream = null;
      }
    }
  }
}



